import {
    ChangeDetectionStrategy, Component, EventEmitter, Input,
    Output, ViewChild,
    ChangeDetectorRef, inject,
    OnDestroy
} from "@angular/core";
import { MatFabButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import {
    FormGroup, ReactiveFormsModule
} from "@angular/forms";
import { ButtonDirective } from "../../../application/directives/button/button.directive";
import { EnhancedFormDirective } from "../../enhanced-form.directive";

/**
 * Sub formComponent manages the internal sub form for list items in the FormList.
 * Displays the allowed operations Add, Update and Cancel when the sub form is open.
 * Also emits important events to the parent to update its internal data
 */
@Component({
    selector: "mgd-subform",
    imports: [
        ReactiveFormsModule,
        MatFabButton,
        MatIcon,
        EnhancedFormDirective,
        ButtonDirective
    ],
    templateUrl: "./subform.component.html",
    styleUrl: "./subform.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: "mgdSubform"
})
export class SubformComponent implements OnDestroy {

    /**
     * Internal Form Group that will be used to edit a row in the list.
     */
    @Input() formGroup!: FormGroup;

    /**
     * Default values for the form group
     */
    @Input() defaultValues?: any;

    /**
     * Event emitted when the form is submitted.
     * Passes the form values as an argument.
     */
    @Output() submitSubform = new EventEmitter<any>();

    /**
     * Event emitted when the form is submitted after an edit operation
     * Passes the form values and the index as arguments
     */
    @Output() editSubform = new EventEmitter<{
        formGroup: FormGroup;
        index: number;
    }>();

    /**
     * Event emitted when the Form Group is closed
     */
    @Output() closed = new EventEmitter<void>();

    /**
     * Event emitted when the Form Group is opened
     */
    @Output() opened = new EventEmitter<void>();

    /**
     * EnhancedFormDirected for the internal Form Group
     */
    @ViewChild(EnhancedFormDirective) form?: EnhancedFormDirective;

    /**
     * open variable to control the display of the subform
     */
    open: boolean = false;

    /**
     * index of the item in the parent form list
     * Used to identify the item when editing
     */
    index?: number;

    private readonly _cdRef = inject(ChangeDetectorRef);

    private readonly _parentForm? = inject(EnhancedFormDirective, {
        optional: true,
        host: true
    });

    /**
     * Handles the proper output and respective data needed when the Subform Submit is called
     * @returns void
     */
    onSubmitClick(): void {

        // Validate form Group
        if (this.formGroup.invalid) {
            return;
        }
        else {
            // If valid emit values
            if (this.index !== undefined) {
                this.editSubform.emit({
                    formGroup: this.formGroup.value,
                    index: this.index
                });
            }
            else {
                this.submitSubform.emit(this.formGroup.value);
            }
        }
        // Clear Form Group
        this.closeSubform();
    }

    onCancel(): void {
        // Cancel button clicked
        this.closeSubform();
    }

    /**
     * Called by the form list control
     *
     * If values is provided populate the subform with the values and display edit action
     *
     * If values is not provided set default values and display add action button
     */
    openSubform(values?: any, index?: number): void {
        this.open = true;
        this.index = index;
        this.opened.emit();
        const formValues = values ?? this.defaultValues;
        this._parentForm?.setDisabled(true);
        this._cdRef.detectChanges();
        if (this.form && formValues) {
            this._populateForm(formValues);
        }
    }

    /*
     * closeSubform called to reset form group values, and clear local variables and emit the close event
     */
    closeSubform(): void {
        // Clear Form Group
        this.formGroup.reset();
        this.index = undefined;
        // Close Form
        this.open = false;
        this._parentForm?.setDisabled(false);
        this.closed.emit();
        this._cdRef.markForCheck();
    }

    /**
     * Angular onDestroy Lifecycle event
     *
     * `closeSubform()` is the only place that normally releases the parent form's disable
     * blocker (via `this._parentForm?.setDisabled(false)`), but it only runs when the user
     * explicitly submits or cancels. Consumers commonly render `<mgd-subform>` inside
     * conditionally-rendered template blocks (e.g. an `@if` section that can be collapsed by
     * unrelated state changes), so Angular can destroy this component while the subform is still
     * `open` and `closeSubform()` never runs. Without this hook, the parent form's disable
     * blocker would never be released, permanently disabling the parent form.
     *
     * Only the disable blocker is released here - `formGroup.reset()` and the `closed` event are
     * intentionally skipped, since the component is being torn down, not user-cancelled.
     *
     * If `closeSubform()` already ran before destroy, `open` is already `false` and this is a
     * no-op, avoiding a double release of the parent's disable count.
     */
    ngOnDestroy(): void {

        if (this.open) {
            this._parentForm?.setDisabled(false);
        }
    }

    /**
     * populates internal form group with provided values
     * @param currentValues
     */
    private _populateForm(currentValues: unknown): void {
        const formValues: Map<string, unknown> = new Map<string, unknown>();
        Object.entries(currentValues).forEach(([key, value]) => {
            formValues.set(key, value);
        });
        this.form?.populate(formValues);
    }
}
